/**
 * Inline SVG icons for the Experience Journey section.
 *
 * Updated to match actual company work:
 *   MB Energy          – fuel logistics: tanker trucks, diesel/gasoline/fuel storage, CO₂ certificate trading
 *   EWE Trading        – renewable energy sources + intraday algorithmic trading strategy
 *   Vattenfall (renew) – wind & solar energy optimisation and trading
 *   Vattenfall (data)  – quantitative data analytics in energy markets
 */

/* ─── Shared gradient definition (rendered once per icon) ─── */
const IconGrad = ({ id }) => (
  <defs>
    <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%"   stopColor="#10b981" />
      <stop offset="100%" stopColor="#06b6d4" />
    </linearGradient>
  </defs>
);

/* ─── Keyframe styles (injected once) ─── */
const iconStyles = `
  @keyframes intradayPulse {
    0%,100% { opacity: 0.75; }
    50%     { opacity: 1;    }
  }
  @keyframes barRise {
    0%,100% { transform: scaleY(1);    }
    50%     { transform: scaleY(1.12); }
  }
  @keyframes turbineSpin {
    from { transform: rotate(0deg);   }
    to   { transform: rotate(360deg); }
  }
  @media (prefers-reduced-motion: reduce) {
    .icon-intraday-anim,
    .icon-bar-anim,
    .icon-blade-anim { animation: none !important; }
  }
`;

let stylesInjected = false;
function ensureStyles() {
  if (stylesInjected || typeof document === 'undefined') return;
  const tag = document.createElement('style');
  tag.textContent = iconStyles;
  document.head.appendChild(tag);
  stylesInjected = true;
}

/* ─── MB Energy: Fuel Tanker Truck ─────────────────────────────────────── */
export function TruckLogisticsIcon() {
  ensureStyles();
  return (
    <svg viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" className="w-full h-full">
      <IconGrad id="ogTruck" />

      {/* Tanker cylinder body */}
      <rect x="16" y="14" width="24" height="17" rx="5" stroke="url(#ogTruck)" strokeWidth="1.7" />
      {/* Internal compartment dividers — 3 fuel sections (diesel / gasoline / other) */}
      <line x1="23" y1="15.5" x2="23" y2="29.5" stroke="url(#ogTruck)" strokeWidth="0.9" strokeLinecap="round" opacity="0.4" />
      <line x1="30" y1="15.5" x2="30" y2="29.5" stroke="url(#ogTruck)" strokeWidth="0.9" strokeLinecap="round" opacity="0.4" />

      {/* Cab */}
      <path d="M4 31 L4 22 L10 17 L16 17 L16 31 Z" stroke="url(#ogTruck)" strokeWidth="1.7" strokeLinejoin="round" />
      {/* Windshield glass */}
      <path d="M5 22 L10 17.5 L15.5 17.5 L15.5 22 Z" stroke="url(#ogTruck)" strokeWidth="1" strokeLinejoin="round" opacity="0.4" />

      {/* Chassis rail */}
      <line x1="4" y1="31" x2="40" y2="31" stroke="url(#ogTruck)" strokeWidth="1.3" strokeLinecap="round" opacity="0.4" />

      {/* Wheels */}
      <circle cx="9"  cy="36" r="3.8" stroke="url(#ogTruck)" strokeWidth="1.6" />
      <circle cx="27" cy="36" r="3.8" stroke="url(#ogTruck)" strokeWidth="1.6" />
      <circle cx="36" cy="36" r="3.8" stroke="url(#ogTruck)" strokeWidth="1.6" />
      {/* Wheel hubs */}
      <circle cx="9"  cy="36" r="1.3" fill="url(#ogTruck)" opacity="0.5" />
      <circle cx="27" cy="36" r="1.3" fill="url(#ogTruck)" opacity="0.5" />
      <circle cx="36" cy="36" r="1.3" fill="url(#ogTruck)" opacity="0.5" />
    </svg>
  );
}

/* ─── EWE Trading: Renewable Energy + Intraday Strategy ─────────────────── */
export function RenewablesTradingIcon() {
  ensureStyles();
  // Sun at (22, 11), r=5. Rays inner r=7, outer r=9.5
  const sx = 22;
  const sy = 11;
  const rays = [
    { x1: sx,     y1: sy - 7,   x2: sx,     y2: sy - 9.5, op: 1    }, // top
    { x1: sx + 5, y1: sy - 5,   x2: sx + 6.7, y2: sy - 6.7, op: 1  }, // top-right
    { x1: sx + 7, y1: sy,       x2: sx + 9.5, y2: sy,      op: 1    }, // right
    { x1: sx + 5, y1: sy + 5,   x2: sx + 6.7, y2: sy + 6.7, op: 0.3 }, // bottom-right (faded — chart below)
    { x1: sx,     y1: sy + 7,   x2: sx,     y2: sy + 9.5, op: 0.2  }, // bottom (hidden by chart)
    { x1: sx - 5, y1: sy + 5,   x2: sx - 6.7, y2: sy + 6.7, op: 0.3 }, // bottom-left (faded)
    { x1: sx - 7, y1: sy,       x2: sx - 9.5, y2: sy,      op: 1    }, // left
    { x1: sx - 5, y1: sy - 5,   x2: sx - 6.7, y2: sy - 6.7, op: 1  }, // top-left
  ];

  return (
    <svg viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" className="w-full h-full">
      <IconGrad id="ogRenew" />

      {/* ── Renewable energy sun (upper half) ── */}
      <circle cx={sx} cy={sy} r="5" stroke="url(#ogRenew)" strokeWidth="1.6" />
      {rays.map(({ x1, y1, x2, y2, op }, i) => (
        <line key={i} x1={x1} y1={y1} x2={x2} y2={y2}
          stroke="url(#ogRenew)" strokeWidth="1.4" strokeLinecap="round" opacity={op} />
      ))}

      {/* ── Intraday trading chart (lower half) ── */}
      {/* Baseline */}
      <line x1="3" y1="41" x2="41" y2="41" stroke="url(#ogRenew)" strokeWidth="1" strokeLinecap="round" opacity="0.25" />
      {/* Volatile intraday price line — animated opacity pulse */}
      <polyline
        className="icon-intraday-anim"
        style={{ animation: 'intradayPulse 2.5s ease-in-out infinite' }}
        points="3,39 7,33 10,37 14,27 17,33 20,26 23,32 27,22 30,29 33,23 37,29 41,21"
        stroke="url(#ogRenew)"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ─── Vattenfall: Wind Turbine + Solar Panel ─────────────────────────────── */
export function SolarWindIcon() {
  ensureStyles();
  return (
    <svg viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" className="w-full h-full">
      <IconGrad id="ogWind" />

      {/* ── Wind turbine (left half, hub at 11,18) ── */}
      {/* Tapered tower */}
      <path d="M9.5 42 L11 18 L12.5 42" stroke="url(#ogWind)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      {/* Tower base */}
      <line x1="7" y1="42" x2="16" y2="42" stroke="url(#ogWind)" strokeWidth="1.4" strokeLinecap="round" opacity="0.5" />
      {/* Hub */}
      <circle cx="11" cy="18" r="2" stroke="url(#ogWind)" strokeWidth="1.6" />
      {/* Spinning blades */}
      <g
        className="icon-blade-anim"
        style={{ transformOrigin: '11px 18px', animation: 'turbineSpin 8s linear infinite' }}
      >
        {/* Blade 1 — upward */}
        <path d="M11,18 C10,13 9,9 11,7 C13,9 12,13 11,18Z" stroke="url(#ogWind)" strokeWidth="1.4" strokeLinejoin="round" />
        {/* Blade 2 — lower-right (120°) */}
        <path d="M11,18 C15,20.5 18,22 19,24 C17.5,25 14,22.5 11,18Z" stroke="url(#ogWind)" strokeWidth="1.4" strokeLinejoin="round" />
        {/* Blade 3 — lower-left (240°) */}
        <path d="M11,18 C7,20.5 4,22 3,24 C4.5,25 8,22.5 11,18Z" stroke="url(#ogWind)" strokeWidth="1.4" strokeLinejoin="round" />
      </g>

      {/* ── Solar panel (right half, centred at ~33,18) ── */}
      {/* Panel frame */}
      <rect x="26" y="13" width="15" height="10" rx="1.5" stroke="url(#ogWind)" strokeWidth="1.7" />
      {/* Panel cell grid — 2 vertical dividers, 1 horizontal */}
      <line x1="31" y1="13" x2="31" y2="23" stroke="url(#ogWind)" strokeWidth="0.85" strokeLinecap="round" opacity="0.45" />
      <line x1="36" y1="13" x2="36" y2="23" stroke="url(#ogWind)" strokeWidth="0.85" strokeLinecap="round" opacity="0.45" />
      <line x1="26" y1="18" x2="41" y2="18" stroke="url(#ogWind)" strokeWidth="0.85" strokeLinecap="round" opacity="0.45" />
      {/* Centre-pole mount */}
      <line x1="33" y1="23" x2="33" y2="34" stroke="url(#ogWind)" strokeWidth="1.5" strokeLinecap="round" opacity="0.7" />
      {/* Ground base */}
      <line x1="28" y1="34" x2="38" y2="34" stroke="url(#ogWind)" strokeWidth="1.3" strokeLinecap="round" opacity="0.5" />

      {/* ── Small sun above panel ── */}
      <circle cx="33" cy="7" r="2.5" stroke="url(#ogWind)" strokeWidth="1.3" opacity="0.75" />
      <line x1="33" y1="3.5" x2="33"  y2="2"   stroke="url(#ogWind)" strokeWidth="1.2" strokeLinecap="round" opacity="0.65" />
      <line x1="36"  y1="4.5" x2="37" y2="3.5" stroke="url(#ogWind)" strokeWidth="1.2" strokeLinecap="round" opacity="0.65" />
      <line x1="37"  y1="7"   x2="39" y2="7"   stroke="url(#ogWind)" strokeWidth="1.2" strokeLinecap="round" opacity="0.65" />
      <line x1="30"  y1="4.5" x2="29" y2="3.5" stroke="url(#ogWind)" strokeWidth="1.2" strokeLinecap="round" opacity="0.65" />
      <line x1="29"  y1="7"   x2="27" y2="7"   stroke="url(#ogWind)" strokeWidth="1.2" strokeLinecap="round" opacity="0.65" />
    </svg>
  );
}

/* ─── Vattenfall Data Analyst: Energy Data Analytics ────────────────────── */
export function DataChartIcon() {
  ensureStyles();
  return (
    <svg viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" className="w-full h-full">
      <IconGrad id="ogData" />
      {/* Bar chart — 4 bars with staggered rise animation */}
      {[
        { x: 5,  h: 16, delay: '0s'   },
        { x: 13, h: 24, delay: '0.2s' },
        { x: 21, h: 18, delay: '0.4s' },
        { x: 29, h: 28, delay: '0.6s' },
      ].map(({ x, h, delay }) => (
        <rect
          key={x}
          x={x}
          y={42 - h}
          width="7"
          height={h}
          rx="2"
          stroke="url(#ogData)"
          strokeWidth="1.5"
          className="icon-bar-anim"
          style={{
            transformOrigin: `${x + 3.5}px 42px`,
            animation: `barRise 2.2s ${delay} ease-in-out infinite`,
          }}
        />
      ))}
      {/* Overlay trend line (data insight) */}
      <polyline
        points="8.5,26 16.5,18 24.5,24 32.5,14"
        stroke="url(#ogData)"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.7"
      />
      {/* X-axis */}
      <line x1="3" y1="42" x2="38" y2="42" stroke="url(#ogData)" strokeWidth="1.3" strokeLinecap="round" opacity="0.3" />
    </svg>
  );
}

/**
 * Resolve the correct icon component for an experience entry.
 * @param {{ company: string, role: string }} exp
 * @returns {React.ComponentType|null}
 */
export function resolveIcon(exp) {
  if (!exp) return null;
  const company = exp.company || '';
  if (company === 'MB Energy')   return TruckLogisticsIcon;
  if (company === 'EWE Trading') return RenewablesTradingIcon;
  if (company.includes('Vattenfall')) {
    const role = (exp.role || '').toLowerCase();
    if (role.includes('renew') || role.includes('optim')) return SolarWindIcon;
    return DataChartIcon;
  }
  return null;
}
